import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { increment, decrement, reset } from '../redux/counterSlice';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button } from 'react-bootstrap';

// Context API Implementation for Comparison
const CounterContext = React.createContext();

const CounterProvider = ({ children }) => {
  const [count, setCount] = useState(0);

  const incrementContext = () => {
    console.log('🟢 Context: Incrementing count from', count);
    setCount(count + 1);
  };

  const decrementContext = () => {
    console.log('🟢 Context: Decrementing count from', count);
    setCount(count - 1);
  };

  const resetContext = () => {
    console.log('🟢 Context: Resetting count');
    setCount(0);
  };

  return (
    <CounterContext.Provider value={{ count, incrementContext, decrementContext, resetContext }}>
      {children}
    </CounterContext.Provider>
  );
};

// Redux Counter Component
const ReduxCounter = () => {
  const count = useSelector((state) => state.counter.value);
  const dispatch = useDispatch();

  return (
    <Card>
      <Card.Header className="bg-danger text-white">Redux Counter</Card.Header>
      <Card.Body className="text-center">
        <h2 className="display-3">{count}</h2>
        <div className="d-flex gap-2 justify-content-center">
          <Button onClick={() => dispatch(increment())} variant="success">+</Button>
          <Button onClick={() => dispatch(decrement())} variant="warning">-</Button>
          <Button onClick={() => dispatch(reset())} variant="secondary">Reset</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

// Context Counter Component
const ContextCounter = () => {
  const { count, incrementContext, decrementContext, resetContext } = React.useContext(CounterContext);

  return (
    <Card>
      <Card.Header className="bg-success text-white">Context API Counter</Card.Header>
      <Card.Body className="text-center">
        <h2 className="display-3">{count}</h2>
        <div className="d-flex gap-2 justify-content-center">
          <Button onClick={incrementContext} variant="success">+</Button>
          <Button onClick={decrementContext} variant="warning">-</Button>
          <Button onClick={resetContext} variant="secondary">Reset</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const Lesson10_Redux = () => {
  useEffect(() => {
    console.log('📖 Lesson 10: Redux vs Context API - Loaded');
    console.log('📊 Comparing two state management approaches');
  }, []);

  return (
    <CounterProvider>
      <LessonLayout 
        lessonNumber={10}
        title="Redux vs Context API" 
        description="Compare Redux and Context API for state management"
      >
        <Alert variant="success">
          <strong>What You'll Learn:</strong>
          <ul className="mb-0 mt-2">
            <li>Redux setup with Redux Toolkit</li>
            <li>Context API for state management</li>
            <li>Side-by-side comparison</li>
            <li>When to use Redux vs Context</li>
          </ul>
        </Alert>

        <Row className="mt-4">
          <Col md={6}>
            <ReduxCounter />
          </Col>
          <Col md={6}>
            <ContextCounter />
          </Col>
        </Row>

        <h4 className="mt-4">📊 Comparison Table</h4>
        <Card>
          <Card.Body>
            <table className="table comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Redux</th>
                  <th>Context API</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Setup Complexity</td>
                  <td>⚠️ More boilerplate</td>
                  <td>✅ Simple setup</td>
                </tr>
                <tr>
                  <td>Performance</td>
                  <td>✅ Optimized</td>
                  <td>⚠️ May cause re-renders</td>
                </tr>
                <tr>
                  <td>DevTools</td>
                  <td>✅ Excellent</td>
                  <td>❌ Limited</td>
                </tr>
                <tr>
                  <td>Middleware</td>
                  <td>✅ Built-in support</td>
                  <td>❌ No built-in support</td>
                </tr>
                <tr>
                  <td>Best For</td>
                  <td>Large apps, complex state</td>
                  <td>Simple state sharing</td>
                </tr>
              </tbody>
            </table>
          </Card.Body>
        </Card>

        <Alert variant="warning" className="mt-4">
          <strong>💡 When to Use Each:</strong>
          <ul className="mb-0">
            <li><strong>Redux:</strong> Large applications, complex state logic, time-travel debugging</li>
            <li><strong>Context:</strong> Simple state sharing, theme/auth, small to medium apps</li>
            <li>You can use both together in the same app!</li>
          </ul>
        </Alert>
      </LessonLayout>
    </CounterProvider>
  );
};

export default Lesson10_Redux;
